import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { queryKeys } from "@/lib/query";
import { suppressGlobalErrorToastMeta } from "@/lib/query";
import { hasEntityPublicKey } from "@/utils/helpers";
import type { PoolChainState, SettlementTokenAmount } from "@/types/api/chain";

export function fetchSettlementTokenBalance(address: string): Promise<number> {
  return apiClient
    .getData<SettlementTokenAmount>(`/chain/settlement-token/balance/${address}`)
    .then((data) => data.amount);
}

export function fetchSettlementTokenSupply(): Promise<number> {
  return apiClient
    .getData<SettlementTokenAmount>("/chain/settlement-token/supply")
    .then((data) => data.amount);
}

export function useEURMBalance(address: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.blockchain.balance(address ?? ""),
    queryFn: () => fetchSettlementTokenBalance(address!),
    enabled: hasEntityPublicKey(address),
    meta: suppressGlobalErrorToastMeta,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function usePoolChainState(poolId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.blockchain.chainState(poolId ?? ""),
    queryFn: () => apiClient.getData<PoolChainState>(`/pools/${poolId}/chain-state`),
    enabled: !!poolId,
    meta: suppressGlobalErrorToastMeta,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useResidualCash(poolId: string | null | undefined) {
  const query = usePoolChainState(poolId);
  return {
    ...query,
    data: query.data ? query.data.residualCash : undefined,
  };
}

export function useTotalSupply(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.blockchain.totalSupply(),
    queryFn: fetchSettlementTokenSupply,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}
